/**
 * Наблюдения за прогоном набора и сложение метрик нескольких прогонов.
 *
 * Режим, состоящий из коротких прогонов, показывает пользователю один
 * результат: метрики складываются, а не берутся от последнего прогона.
 * Пакет не знает, ради чего был прогон, — он считает, а решение о том, к
 * какой учебной единице относится результат, принимает продуктовая функция.
 */

import type { KeystrokeEvent, TypingError, TypingMetrics } from '@type2x/shared-contracts';
import { CHARACTERS_PER_WORD, EMPTY_METRICS } from '@type2x/shared-contracts';

import { median } from './metrics.js';

/** Миллисекунд в минуте. */
const MS_PER_MINUTE = 60_000;

/** Наблюдения за одним прогоном: то, из чего складывается агрегат единицы. */
export interface RunObservation {
  readonly attempts: number;
  readonly errors: number;
  readonly corrections: number;
  readonly medianLatencyMs: number;
}

/** Пустое наблюдение — начальное состояние единицы. */
export const EMPTY_OBSERVATION: RunObservation = Object.freeze({
  attempts: 0,
  errors: 0,
  corrections: 0,
  medianLatencyMs: 0,
});

/**
 * Собирает наблюдение по одному прогону.
 *
 * Прогон существует ради одной учебной единицы, поэтому его ошибки относятся
 * к ней целиком — в том числе когда набирается предложение, а единица в нём
 * лишь одно слово. Ошибок при этом не может быть больше, чем попыток: доля
 * ошибок выше единицы не имеет смысла.
 *
 * @param attempts сколько символов единицы засчитывается прогону;
 * @param events нажатия прогона;
 * @param errors ошибки прогона.
 */
export function observeRun(
  attempts: number,
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): RunObservation {
  const latencies = events
    .map((event) => event.interKeyIntervalMs)
    .filter((interval) => interval > 0);

  return {
    attempts,
    errors: Math.min(errors.length, attempts),
    corrections: events.filter((event) => event.corrected).length,
    medianLatencyMs: median(latencies),
  };
}

/** Складывает наблюдения по одной единице. */
export function mergeObservations(previous: RunObservation, next: RunObservation): RunObservation {
  return {
    attempts: previous.attempts + next.attempts,
    errors: previous.errors + next.errors,
    corrections: previous.corrections + next.corrections,
    // Медиана медиан — приближение, но оно устойчиво к одному медленному
    // прогону, а точное распределение задержек на сервер и не уходит.
    medianLatencyMs: median([previous.medianLatencyMs, next.medianLatencyMs].filter((v) => v > 0)),
  };
}

/** Складывает метрики прогонов в метрики сессии. */
export function mergeMetrics(previous: TypingMetrics, next: TypingMetrics): TypingMetrics {
  const typedCharacters = previous.typedCharacters + next.typedCharacters;
  const correctCharacters = previous.correctCharacters + next.correctCharacters;
  const elapsedMs = previous.elapsedMs + next.elapsedMs;

  const minutes = elapsedMs / MS_PER_MINUTE;
  const cpm = minutes > 0 ? correctCharacters / minutes : 0;

  // Точность набора складывается по числу нажатий, а не по длине текста:
  // прогон с двадцатью исправлениями сделал больше нажатий, чем оставил
  // символов, и весить он должен по сделанному.
  const keystrokes = previous.keystrokes + next.keystrokes;
  const firstTryCorrect =
    previous.firstTryAccuracy * previous.keystrokes + next.firstTryAccuracy * next.keystrokes;

  return {
    wpm: cpm / CHARACTERS_PER_WORD,
    cpm,
    accuracy: typedCharacters > 0 ? correctCharacters / typedCharacters : EMPTY_METRICS.accuracy,
    firstTryAccuracy:
      keystrokes > 0 ? firstTryCorrect / keystrokes : EMPTY_METRICS.firstTryAccuracy,
    keystrokes,
    typedCharacters,
    correctCharacters,
    corrections: previous.corrections + next.corrections,
    rhythmStability: weightedRhythm(previous, next, typedCharacters),
    elapsedMs,
  };
}

/** Ритм усредняется по объёму: короткий прогон не весит как длинный. */
function weightedRhythm(
  previous: TypingMetrics,
  next: TypingMetrics,
  typedCharacters: number,
): number {
  if (typedCharacters === 0) return 0;

  return (
    (previous.rhythmStability * previous.typedCharacters +
      next.rhythmStability * next.typedCharacters) /
    typedCharacters
  );
}
